/**
 * apps/frontend/src/api/client.ts — axios 기반 backend API 클라이언트.
 *
 * auth v1.5 (httpOnly cookie cutover):
 *   - 인증은 cookie 자동 (`withCredentials: true`) — Authorization 헤더 부착 X
 *   - 401 + `code === 'TOKEN_EXPIRED'` → /auth/refresh POST (body empty, cookie 자동) → 원 요청 재시도
 *   - module-level single-flight: 다중 401 race 시에도 refresh 호출은 1회
 *   - refresh 실패 → clearSession + AuthGuard 가 /login redirect
 *   - 503 + `AUTH_INFRA_UNAVAILABLE` → 자동 refresh 우회 (caller 가 토스트). 무한 루프 차단.
 */

import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore, broadcastTokensRefreshed, onTokensRefreshedOk } from '../store/authStore';
import { toast } from '../components/shared/Toast';
import { withCrossTabLock, LOCK_AUTH_REFRESH } from '../lib/cross-tab-lock';
import { markSessionEndReason } from '../lib/session-end-reason';
// Cycle 25 — trace_id / session_id 자동 부착
import { newTraceId, getSessionId } from '../lib/trace-context';
// Cycle 47 — telemetry endpoint URL SSOT (cycle 46 신규 학습 layer 3).
import { TELEMETRY_TRACE_MISMATCH_PATH } from '@workchat/shared-types';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
});

// Cycle 25/26 — request interceptor: 매 요청에 X-Trace-Id (요청 단위) + X-Session-Id (탭 단위) 부착.
// caller 가 명시적으로 X-Trace-Id 헤더 set 한 경우는 우선 (e2e test 등).
apiClient.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  if (!config.headers['X-Trace-Id'] && !config.headers['x-trace-id']) {
    config.headers['X-Trace-Id'] = newTraceId();
  }
  if (!config.headers['X-Session-Id'] && !config.headers['x-session-id']) {
    config.headers['X-Session-Id'] = getSessionId();
  }
  return config;
});

// Cycle 26/27 — trace_id 일관성 모니터.
// drift 발견 시 console.warn + counter 증가. Cycle 27 — 10초마다 batch 로 backend telemetry endpoint 에 전송.
// Cycle 51 — beforeunload flush 추가: 페이지 종료 직전 pending batch 손실 차단.
//   navigator.sendBeacon 우선 (fetch keepalive 대안 — 페이지 종료 후에도 발행 보장).
//   환경 (jsdom/SSR) 에 sendBeacon 미존재 시 keepalive fetch fallback. 모두 silent fail.
let traceMismatchCount = 0;
let pendingMismatchBatch = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let unloadHandlerInstalled = false;

export function _getTraceMismatchCountForTests(): number { return traceMismatchCount; }
export function _resetTraceMismatchForTests(): void {
  traceMismatchCount = 0;
  pendingMismatchBatch = 0;
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
}
export function _getPendingMismatchBatchForTests(): number { return pendingMismatchBatch; }

const BATCH_FLUSH_MS = 10_000;
const BASE_URL_FOR_BEACON = baseURL;  // '/api/v1' 또는 VITE_API_BASE_URL — beforeunload sendBeacon 용

function scheduleMismatchFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const toSend = Math.min(pendingMismatchBatch, 100);  // 1 batch 최대 100
    if (toSend <= 0) return;
    pendingMismatchBatch -= toSend;
    // best-effort POST — 실패해도 다음 batch 에 다시 시도 (counter 보존 X — 운영 telemetry 라 손실 허용)
    apiClient.post(TELEMETRY_TRACE_MISMATCH_PATH, { count: toSend }).catch(() => {
      // network/auth 실패 시 silent — 운영 telemetry 손실 허용
    });
  }, BATCH_FLUSH_MS);
}

/**
 * Cycle 51 — 페이지 종료 직전 pending batch 강제 발송.
 *
 * setTimeout 기반 batch 가 unload 이벤트로 cancel 되면 마지막 ~10 초의 mismatch 가 silent loss.
 * sendBeacon 또는 keepalive fetch 으로 동기 발사 보장.
 *
 * silent loss family (cycle 50 meta-index) 의 telemetry domain 봉합 — best-effort
 * silent 정책의 page unload 표면을 명시 가시화.
 */
function flushMismatchOnUnload(): void {
  if (pendingMismatchBatch <= 0) return;
  const toSend = Math.min(pendingMismatchBatch, 100);
  pendingMismatchBatch -= toSend;
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  try {
    const url = `${BASE_URL_FOR_BEACON}${TELEMETRY_TRACE_MISMATCH_PATH}`;
    const body = JSON.stringify({ count: toSend });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => { /* silent */ });
    }
  } catch { /* silent fail — telemetry 손실 허용, 사용자 흐름 방해 X */ }
}

function ensureUnloadHandler(): void {
  if (unloadHandlerInstalled || typeof window === 'undefined') return;
  unloadHandlerInstalled = true;
  // 'pagehide' 가 모바일 브라우저에서 더 신뢰성 있음. beforeunload 도 같이 등록 (둘 다 발화 정상).
  window.addEventListener('pagehide', flushMismatchOnUnload);
  window.addEventListener('beforeunload', flushMismatchOnUnload);
}

/** test 격리 — unload handler 재설치 가능. */
export function _resetUnloadHandlerInstalledForTests(): void {
  unloadHandlerInstalled = false;
}

/** test 진단 — 직접 flush 호출. */
export function _flushMismatchOnUnloadForTests(): void {
  flushMismatchOnUnload();
}

apiClient.interceptors.response.use(
  (res) => {
    try {
      const sent = res.config?.headers?.['X-Trace-Id'] ?? res.config?.headers?.['x-trace-id'];
      const received = res.headers?.['x-trace-id'] ?? res.headers?.['X-Trace-Id'];
      // 양쪽 모두 있고 다를 때만 drift (backend 가 자체 생성한 trace 가 더 우선)
      if (sent && received && typeof sent === 'string' && typeof received === 'string') {
        const sentNorm = sent.toLowerCase();
        const recvNorm = received.toLowerCase();
        if (sentNorm !== recvNorm) {
          traceMismatchCount++;
          pendingMismatchBatch++;
          scheduleMismatchFlush();
          ensureUnloadHandler();  // Cycle 51 — 첫 mismatch 시 unload handler 설치 (page unload silent loss 차단)
          // dev/prod 모두 console.warn — 운영자가 browser devtools 로 진단
          // eslint-disable-next-line no-console
          console.warn(
            `[trace] mismatch: sent=${sentNorm.slice(0, 8)}… received=${recvNorm.slice(0, 8)}…  url=${res.config.url}`,
          );
        }
      }
    } catch {
      // 모니터 자체 실패는 응답 흐름 방해 X
    }
    return res;
  },
  // error path 는 별도 — 응답 자체가 없을 가능성 + 다른 interceptor 들이 처리.
);

/**
 * 2026-08-03 — HTTP 200 으로 위장된 rate-limit 응답을 429 로 승격.
 *
 * 증상: 메뉴 이동·새로고침 중 **간헐적으로 데이터만 안 나온다**(에러도 토스트도 없음).
 *
 * 원인: 서버가 요청 한도를 넘겼을 때 429 가 아니라 `200 OK` + 본문
 *   `{"statusCode":429,"code":"RATE_LIMITED",...}` 를 내려보냈다. chat-server 의 error handler 가
 *   @fastify/rate-limit 이 throw 한 **plain object**(Error 아님)를 페이로드로 간주해 직렬화한 탓.
 *   axios 는 이를 성공으로 처리하므로 `res.data.<필드>` 가 undefined 가 되고, 호출부 대부분이
 *   `Array.isArray(...) ? ... : []` 가드로 조용히 빈 목록으로 강등해 화면만 비어 보였다.
 *   아래 인터셉터의 429 백오프·토스트 경로는 `status === 429` 조건이라 한 번도 발동하지 못했다.
 *
 * 조치: 200 이면서 본문이 rate-limit 시그니처(`code === 'RATE_LIMITED'` + `statusCode === 429`)면
 *   429 AxiosError 로 승격 → 아래 인터셉터의 **기존** 재시도·토스트 경로를 그대로 태운다.
 *   서버측 봉합(chat-server lib/thrown-status.ts)과 별개로 유지한다 — 구버전 서버·캐시된 응답·
 *   중간 프록시가 같은 형태를 돌려줄 때의 안전망.
 *
 * 등록 위치는 아래 auth/429 인터셉터보다 **앞** 이어야 한다 (여기서 reject 한 값이 뒤 인터셉터의
 * error handler 로 전달되는 순서).
 */
apiClient.interceptors.response.use((res) => {
  const body = res.data as { statusCode?: unknown; code?: unknown } | null | undefined;
  const isDisguisedRateLimit =
    res.status === 200 &&
    body != null &&
    typeof body === 'object' &&
    body.code === 'RATE_LIMITED' &&
    body.statusCode === 429;

  if (!isDisguisedRateLimit) return res;

  return Promise.reject(
    new AxiosError(
      'RATE_LIMITED (HTTP 200 으로 응답된 rate-limit — 429 로 승격)',
      AxiosError.ERR_BAD_REQUEST,
      res.config,
      res.request,
      // 헤더(retry-after 등)는 원본 유지 — 아래 인터셉터가 백오프 계산에 사용.
      { ...res, status: 429 },
    ),
  );
});

// 인증 endpoint 는 401 이 정상 응답 (잘못된 비밀번호 등) — 인터셉터 우회.
//
// ★ `verify-password` 를 2026-08-28 에 더했다 — 화면잠금 해제다.
//   빠져 있는 동안 **비밀번호를 한 번만 틀려도 로그아웃**됐다: 서버가 401 INVALID_PASSWORD 를
//   주면 여기서 «되살릴 수 없는 401» 로 보고 곧장 `clearSession()` 을 불렀다(잠금 화면이 세는
//   5회는 닿지도 못했다). 이 라우트의 401 은 «틀렸다» 라는 답이지 세션이 죽었다는 뜻이 아니다.
const AUTH_ENDPOINT_PATTERN = /\/auth\/(login|token\/refresh|logout|sso|mfa|verify-password)/;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  /** 429 백오프 재시도 1회 가드 (2026-07-25). */
  _retry429?: boolean;
}

/** error.config 를 재시도용 타입으로 — 인터셉터에서 반복 캐스팅을 피한다. */
function cfgOf(err: AxiosError): RetryConfig | undefined {
  return err.config as RetryConfig | undefined;
}

// module-level single-flight (탭 내) — 같은 탭의 동시 401 이 1회 refresh 만 트리거.
// cross-tab serialize 는 withCrossTabLock 이 추가로 처리 (review BLOCK-C 봉합).
let refreshInFlight: Promise<void> | null = null;

// 다른 탭이 refresh 를 막 끝낸 직후 본 탭이 lock 진입한 경우 redundant refresh 방지.
// BroadcastChannel `tokens-refreshed-ok` 수신 시 갱신되어 본 탭이 짧은 시간 내 lock
// 진입 시 skip 가능. (cross-tab refresh 가 1회로 수렴.)
//
// 채널 책임 단일화 — 자체 BroadcastChannel 인스턴스 대신 authStore 의 listener registry
// 사용 (math-architect ISO-1 봉합).
let lastRefreshSuccessAt = 0;
const RECENT_REFRESH_WINDOW_MS = 2_000;

onTokensRefreshedOk(() => {
  lastRefreshSuccessAt = Date.now();
});

/**
 * /auth/refresh POST. body 비워두고 cookie 자동 전송 (auth v1.5).
 *
 * 다중 호출자가 본 함수를 await 해도:
 *   - 같은 탭 내 동시 호출: module-level refreshInFlight Promise 로 1회만 발생.
 *   - cross-tab 동시 호출: withCrossTabLock 으로 origin 단위 1회만 발생 (review BLOCK-C).
 *   - 다른 탭이 직전 2초 내 refresh 성공: skip (BroadcastChannel `tokens-refreshed-ok` 수신).
 *
 * 성공 시 BroadcastChannel `tokens-refreshed-ok` 로 다른 탭에 통지 → 후속 탭은 자체
 * refresh 호출 시 위 skip 분기 진입.
 */
/**
 * C(8443 갱신 보강) — refresh POST 의 **일시 실패**(네트워크 무응답·타임아웃·5xx;
 * 외부 프록시 구간 blip 포함)는 짧은 지연 후 1회만 재시도한다. 정의된 4xx(401 reuse/
 * invalid/expired refresh 등)는 즉시 throw → 인터셉터가 정상적으로 재로그인 처리.
 *
 * 목적: TTL 정상화(access 60분) 후에도 남는 "헛로그아웃"(일시 네트워크 오류로 인한
 * 세션 종료)을 줄인다. 실패가 지속되면 기존과 동일하게 throw(fail-safe — 동작 회귀 없음).
 */
async function postRefreshWithTransientRetry(): Promise<void> {
  const doPost = () =>
    axios.post(`${baseURL}/auth/token/refresh`, {}, { withCredentials: true, timeout: 30_000 });
  try {
    await doPost();
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    // 무응답(status undefined = 네트워크/타임아웃) 또는 5xx 만 일시 오류로 간주.
    const transient = status === undefined || status >= 500;
    if (!transient) throw err;
    /**
     * ★ 2026-09-14 — **같은 refresh 쿠키로 다시 보내지 않는다**(백엔드 조사 · 관제 후속 ⑤).
     *   서버가 이미 회전했는데 응답만 잃었으면(프록시 502 · 타임아웃) 옛 토큰 재사용으로 판정돼 사용자의
     *   **모든 토큰이 폐기**된다(`refresh-token.service` reuse-detected · grace 없음) — 다른 기기까지 로그아웃.
     *   그래서 세션을 먼저 묻는다: `/users/me` 200 이면 새 쿠키가 이미 들어온 것(회전 성공) → 성공으로 본다.
     *   그 밖(401 · 무응답)은 원래 오류를 던진다 → 인터셉터가 이 탭만 재로그인으로 보낸다.
     *   ⚠ 응답을 잃은 경우 쿠키도 못 받았으니 /users/me 가 401 일 수 있다 — 그때 다시 보내면 바로 그 폐기가 난다.
     */
    const alive = await axios
      .get(`${baseURL}/users/me`, { withCredentials: true, timeout: 10_000 })
      .then((r) => r.status === 200)
      .catch(() => false);
    if (!alive) throw err;
  }
}

export async function refreshAccessToken(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = withCrossTabLock(LOCK_AUTH_REFRESH, async () => {
    try {
      // 다른 탭이 막 refresh 를 끝냈으면 본 탭은 cookie jar 가 이미 갱신됐으므로 skip.
      // (Chrome cookie jar 가 cross-tab 자동 공유 → 본 탭의 다음 요청은 새 cookie 사용.)
      if (Date.now() - lastRefreshSuccessAt < RECENT_REFRESH_WINDOW_MS) {
        return;
      }
      await postRefreshWithTransientRetry();
      lastRefreshSuccessAt = Date.now();
      broadcastTokensRefreshed();
    } finally {
      // refreshInFlight 해제는 lock 외부에서 하지 않고 안에서 (다음 caller 가 lock 획득
      // 시 refreshInFlight 가 이미 null 인 상태로 진입 가능 — 위 skip window 가 2차 가드).
    }
  }).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<{ code?: string }>) => {
    const status = error.response?.status;
    const url = error.config?.url ?? '';
    const isAuthEndpoint = AUTH_ENDPOINT_PATTERN.test(url);

    // 503 AUTH_INFRA_UNAVAILABLE — 백엔드 KMS/Vault/DB 장애. 사용자에게 일관된 메시지를
    // 노출할 수 있도록 그대로 reject (caller 가 토스트). 무한 재시도 차단을 위해 자동
    // refresh 분기 진입하지 않는다 (학습 #4).
    if (status === 503 && error.response?.data?.code === 'AUTH_INFRA_UNAVAILABLE') {
      return Promise.reject(error);
    }

    /**
     * 429 (RATE_LIMITED) — 재시도 후에도 실패하면 **사용자에게 알린다** (2026-07-25 진단).
     *
     * 운영 rate limit 은 per-IP 60/분인데 `TRUST_PROXY` 가 꺼져 있어 키가 엣지 주소 하나
     * (`fastify-rate-limit-127.0.0.1`)로 합산된다 — 즉 접속자 전원이 한 예산을 나눠 쓴다.
     * 화면 하나가 9~20 요청을 쓰는 지금 구조에서는 동시 사용자가 몰리면 초과가 난다.
     * 그런데 호출부 대부분이 실패를 조용히 빈 목록으로 강등해(프로젝트·채팅 도메인 13곳+)
     * "데이터가 간헐적으로 안 나온다"로만 보였다. → 짧게 물러났다 한 번 더 시도하고,
     * 그래도 안 되면 침묵하지 않는다.
     *
     * Retry-After(초)를 존중하되 상한 3초 — 사용자가 체감하는 지연을 제한한다.
     */
    if (status === 429 && cfgOf(error) && !cfgOf(error)!._retry429) {
      const rc = cfgOf(error)!;
      rc._retry429 = true;
      const after = Number(error.response?.headers?.['retry-after']);
      const waitMs = Math.min(Number.isFinite(after) && after > 0 ? after * 1000 : 800, 3000);
      await new Promise((resolve) => setTimeout(resolve, waitMs));
      try {
        return await apiClient.request(rc as AxiosRequestConfig);
      } catch (retryErr) {
        toast.error('요청이 많아 잠시 처리되지 않았습니다. 잠시 후 다시 시도해 주세요.');
        return Promise.reject(retryErr);
      }
    }

    if (status !== 401 || isAuthEndpoint) {
      return Promise.reject(error);
    }

    const cfg = error.config as RetryConfig | undefined;
    const code = error.response?.data?.code;

    /**
     * 복구 가능한 401 (2026-07-25 진단) — refresh 쿠키가 살아 있으면 조용히 되살린다.
     *
     * 왜 TOKEN_EXPIRED 만으로는 부족한가:
     *   access 쿠키(`wc_at`)의 수명이 토큰 만료와 같아서, 만료되는 순간 **브라우저가 쿠키를
     *   지운다**. 그러면 서버는 토큰을 못 받아 `TOKEN_EXPIRED` 가 아니라 `AUTH_REQUIRED`
     *   (쿠키·헤더 모두 없음)를 돌려준다 — 실측: `curl /api/v1/project-teams` → AUTH_REQUIRED.
     *   그런데 종전 코드는 TOKEN_EXPIRED 만 복구 대상으로 봐서, 14일짜리 refresh 쿠키가
     *   멀쩡한데도 세션을 버렸다. 60분 이상 유휴였던 탭이 복귀 첫 요청에서 반드시 실패하고
     *   재로그인을 요구받던 원인(운영 로그: 웹 세션 84개 중 61개가 부팅 프로브 401,
     *   같은 창에서 수동 로그인 47건, 반면 /auth/token/refresh 는 호출되면 74/74 성공).
     *
     * 안전장치: 재시도는 요청당 1회(`_retry`)이고, refresh 자체가 실패하면 기존과 똑같이
     * clearSession → 로그인 유도. 즉 "되살릴 수 있으면 되살리고, 아니면 종전 동작".
     */
    const RECOVERABLE_401 = new Set(['TOKEN_EXPIRED', 'TOKEN_MISSING', 'AUTH_REQUIRED']);

    if ((code === undefined || RECOVERABLE_401.has(code)) && cfg && !cfg._retry) {
      cfg._retry = true;
      try {
        await refreshAccessToken();
        // cookie 가 새로 발급됐으므로 같은 axios config 로 재시도하면 자동 동봉됨.
        return apiClient.request(cfg as AxiosRequestConfig);
      } catch (refreshErr) {
        // refresh 실패 — 만료/revoke/reuse 탐지 등. 세션 정리 후 원 401 전파.
        useAuthStore.getState().clearSession();
        return Promise.reject(error);
      }
    }

    // 복구 불가 401 (TOKEN_INVALID / reuse 탐지 / FORBIDDEN-as-401) 또는 이미 1회 재시도한 요청.
    /**
     * ★ TOKEN_REVOKED (서버 PR #1365 · 2026-09-15) — 비밀번호 변경 · 모든 기기 로그아웃 · 관리자
     *   비활성/초기화 직후. refresh 도 함께 폐기돼 되살릴 수 없으므로 종전대로 세션을 버리되,
     *   로그인 화면이 「왜」를 말하도록 이유를 남긴다. 이미 남은 이유(이 탭의 비밀번호 변경)는 덮지 않는다.
     */
    if (code === 'TOKEN_REVOKED') markSessionEndReason('revoked');
    useAuthStore.getState().clearSession();
    return Promise.reject(error);
  },
);
